import axios from "axios";
import { errorActions } from "../helpers/auth_helper";
import {
  IVendorAgentDashboardListResponse,
  IVendorAgentDashboardDetailResponse,
  IVendorAgentDashboardStatsResponse,
  IVendorAgentDashboardFilters,
  IVendorAgentCartItemsResponse,
  IVendorAgentCartItemsFilters,
} from "../interfaces/vendor_agent_dashboard.interface";

const BASE = `${process.env.REACT_APP_API_URL}/vendor-agent-dashboard`;

/**
 * Fetch a paginated list of vendor agent predictions for the dashboard.
 */
export async function getVendorAgentPredictions(
  filters: IVendorAgentDashboardFilters
): Promise<IVendorAgentDashboardListResponse | undefined> {
  try {
    const params: Record<string, string | number> = {
      page: filters.page ?? 1,
      page_size: filters.page_size ?? 24,
    };
    if (filters.vendor_id) params.vendor_id = filters.vendor_id;
    if (filters.status) params.status = filters.status;
    if (filters.match_method) params.match_method = filters.match_method;
    if (filters.match_correct) params.match_correct = filters.match_correct;
    if (filters.lead_time != null) params.lead_time = filters.lead_time;
    if (filters.since) params.since = filters.since;
    if (filters.until) params.until = filters.until;
    if (filters.search) params.search = filters.search;

    return await axios
      .get<IVendorAgentDashboardListResponse>(`${BASE}/`, { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching vendor agent predictions");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * Fetch full detail for a single prediction (used in the side drawer).
 */
export async function getVendorAgentPredictionDetail(
  predictionId: number
): Promise<IVendorAgentDashboardDetailResponse | undefined> {
  try {
    return await axios
      .get<IVendorAgentDashboardDetailResponse>(`${BASE}/${predictionId}/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching prediction detail");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * Fetch top-of-page aggregate stats for the dashboard.
 */
export async function getVendorAgentDashboardStats(params?: {
  since?: string;
  until?: string;
  vendor_id?: number;
}): Promise<IVendorAgentDashboardStatsResponse | undefined> {
  try {
    return await axios
      .get<IVendorAgentDashboardStatsResponse>(`${BASE}/stats/`, { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching vendor agent stats");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * Update the remarks and/or match_correct flag on a prediction.
 */
export async function updatePredictionRemarks(
  predictionId: number,
  payload: { remarks: string }
): Promise<{ success: boolean; id: number; remarks: string } | undefined> {
  try {
    return await axios
      .patch<{ success: boolean; id: number; remarks: string }>(
        `${BASE}/${predictionId}/remarks/`,
        payload
      )
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error updating prediction remarks");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * Fetch a paginated list of cart items with vendor-agent lifecycle info.
 * One row per CartItemDetails that has at least one prediction.
 */
export async function getVendorAgentCartItems(
  filters: IVendorAgentCartItemsFilters
): Promise<IVendorAgentCartItemsResponse | undefined> {
  try {
    const params: Record<string, string | number> = {
      page: filters.page ?? 1,
      page_size: filters.page_size ?? 25,
    };
    if (filters.vendor_id) params.vendor_id = filters.vendor_id;
    if (filters.step) params.step = filters.step;
    if (filters.search) params.search = filters.search;
    if (filters.since) params.since = filters.since;
    if (filters.until) params.until = filters.until;

    return await axios
      .get<IVendorAgentCartItemsResponse>(`${BASE}/cart-items/`, { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching cart items lifecycle");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}
